import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const LinkedInIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

const HeroSection = () => {
  return (
    <section 
      className="min-h-[35vh] flex items-center bg-[#faf9f7]"
      data-testid="contact-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
          data-testid="contact-hero-title"
        >
          Get in Touch
        </h1>
        <p 
          className="font-dm-sans text-lg md:text-xl text-[#4a4744] leading-relaxed max-w-3xl animate-fade-in-up animate-delay-200"
          data-testid="contact-hero-subtitle"
        >
          We'd love to understand your challenges and explore how we can help create the conditions for meaningful change.
        </p>
      </div>
    </section>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      logger.warn('reCAPTCHA site key not configured');
      setRecaptchaLoaded(true); // Allow form to work without reCAPTCHA in dev
      return;
    }

    // Check if already loaded
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true);
      });
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup not needed as reCAPTCHA should persist
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const executeRecaptcha = useCallback(async () => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) {
      return null; // Skip reCAPTCHA if not configured
    }
    
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact_form' });
      return token;
    } catch (err) {
      logger.error('reCAPTCHA error:', err);
      return null;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha();
      
      // Submit form with reCAPTCHA token
      await axios.post(`${API}/contact`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        organisation: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      logger.error('Error submitting form:', err);
      if (err.response?.data?.detail?.includes('reCAPTCHA') || err.response?.data?.detail?.includes('robot')) {
        setError('Unable to verify you are not a robot. Please try again or contact us directly at contact@agilitie.com.au');
      } else {
        setError('There was an error sending your message. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-[#faf9f7] p-8 text-center" data-testid="contact-success">
        <div className="flex justify-center mb-4">
          <CheckCircle size={48} className="text-[#d4782c]" />
        </div>
        <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a] mb-3">
          Message Sent
        </h3>
        <p className="text-[#4a4744] mb-6">
          Thank you for reaching out. We'll be in touch soon.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="btn-secondary"
          data-testid="send-another-btn"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 text-sm" data-testid="form-error">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="name" className="block font-dm-sans font-medium text-[#1a1a1a] mb-2">
          Name <span className="text-[#d4782c]">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-[#e5e2dc] bg-white text-[#1a1a1a] font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors"
          placeholder="Your name"
          data-testid="input-name"
        />
      </div>

      <div>
        <label htmlFor="email" className="block font-dm-sans font-medium text-[#1a1a1a] mb-2">
          Email <span className="text-[#d4782c]">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-[#e5e2dc] bg-white text-[#1a1a1a] font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors"
          placeholder="your.email@example.com"
          data-testid="input-email"
        />
      </div>

      <div>
        <label htmlFor="organisation" className="block font-dm-sans font-medium text-[#1a1a1a] mb-2">
          Organisation
        </label>
        <input
          type="text"
          id="organisation"
          name="organisation"
          value={formData.organisation}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-[#e5e2dc] bg-white text-[#1a1a1a] font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors"
          placeholder="Your organisation"
          data-testid="input-organisation"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block font-dm-sans font-medium text-[#1a1a1a] mb-2">
          Phone <span className="text-[#4a4744] text-sm font-normal">(optional)</span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-[#e5e2dc] bg-white text-[#1a1a1a] font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors"
          placeholder="+61 400 000 000"
          data-testid="input-phone"
        />
      </div>

      <div>
        <label htmlFor="message" className="block font-dm-sans font-medium text-[#1a1a1a] mb-2">
          Message <span className="text-[#d4782c]">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={6}
          className="w-full px-4 py-3 border border-[#e5e2dc] bg-white text-[#1a1a1a] font-dm-sans focus:outline-none focus:border-[#d4782c] transition-colors resize-none"
          placeholder="Tell us about your challenges and how we might help..."
          data-testid="input-message"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="submit-btn"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
};

const ContactDetails = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      data-testid="contact-details"
    >
      <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a] mb-8">
        Contact Details
      </h2>

      <div className="space-y-6 mb-10">
        {/* Email */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[#faf9f7] flex items-center justify-center text-[#d4782c]">
            <Mail size={20} />
          </div>
          <div>
            <p className="font-dm-sans font-medium text-[#1a1a1a] mb-1">Email</p>
            <a 
              href="mailto:contact@agilitie.com.au"
              className="text-[#d4782c] hover:underline"
              data-testid="contact-email-link"
            >
              contact@agilitie.com.au
            </a>
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[#faf9f7] flex items-center justify-center text-[#d4782c]">
            <Phone size={20} />
          </div>
          <div>
            <p className="font-dm-sans font-medium text-[#1a1a1a] mb-1">Phone</p>
            <a 
              href="tel:1300489850"
              className="text-[#4a4744] hover:text-[#d4782c] transition-colors"
              data-testid="contact-phone-link"
            >
              1300 489 850
            </a>
          </div>
        </div>

        {/* LinkedIn */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[#faf9f7] flex items-center justify-center text-[#d4782c]">
            <LinkedInIcon />
          </div>
          <div>
            <p className="font-dm-sans font-medium text-[#1a1a1a] mb-1">LinkedIn</p>
            <a 
              href="https://www.linkedin.com/company/agilitie/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d4782c] hover:underline"
              data-testid="contact-linkedin-link"
            >
              linkedin.com/company/agilitie
            </a>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[#faf9f7] flex items-center justify-center text-[#d4782c]">
            <MapPin size={20} />
          </div>
          <div>
            <p className="font-dm-sans font-medium text-[#1a1a1a] mb-1">Location</p>
            <p className="text-[#4a4744]">Melbourne, Australia</p>
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="bg-[#faf9f7] border-l-[3px] border-[#d4782c] p-6">
        <p className="text-[#4a4744] leading-relaxed">
          Agilitie works with organisations across Australia. For an initial conversation about how we can help, please reach out via the form or contact us directly.
        </p>
      </div>
    </div>
  );
};

export default function ContactPage() {
  const [formRef, formVisible] = useScrollReveal(0.1);

  return (
    <Layout>
      <SEO 
        title="Contact Us"
        path="/contact"
        description="Get in touch with Agilitie to discuss your organisational transformation challenges. We'd love to understand how we can help."
      />
      <HeroSection />
      <section className="py-16 md:py-24 bg-white" data-testid="contact-content">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column - Form */}
            <div 
              ref={formRef}
              className={`transition-all duration-700 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a] mb-8">
                Send us a Message
              </h2>
              <ContactForm />
            </div>

            {/* Right Column - Details */}
            <ContactDetails />
          </div>
        </div>
      </section>
    </Layout>
  );
}
